// =====================================================================
// Pure helpers for describing the SDR's microphone setup, so we can report it
// (RUM actions + browser logs) and later warn on a bad one.
//
// WHY THIS EXISTS — the pseudo-device problem:
// Chromium synthesises extra `enumerateDevices()` entries that do NOT name a
// physical mic; they TRACK the OS default and silently re-point at whatever the
// OS picks. `deviceId: 'default'` (all platforms) and `'communications'`
// (Windows — tracks the separate Windows "Communications" default, which is
// frequently NOT the multimedia default). Firefox/Safari do not synthesise them.
//
// The consequence: an SDR bound to 'default' who unplugs a headset stays bound
// to 'default' — same deviceId before and after — while the physical mic silently
// changes underneath. Keying anything off the raw selected deviceId therefore
// MISSES the exact transition we're chasing. We must resolve to the real device.
//
// HOW WE RESOLVE — groupId, not label:
// A pseudo entry shares its `groupId` with the real device it points at, so the
// groupId join names the physical mic. We deliberately do NOT parse labels:
// Chromium's default-entry label is locale- and build-dependent (a headless
// Chromium here returned " - External Microphone (Built-in)" — note the "Default"
// word is absent entirely), so any `startsWith('Default - ')` check is a trap.
//
// Verified empirically 2026-07-15 against real Chromium + Firefox on macOS
// (Playwright, real audio hardware):
//   - Chromium: 'default' present for input AND output; its groupId matched the
//     real device exactly; getUserMedia({audio:true}) reports getSettings()
//     .deviceId === 'default' (so even the live track won't name the real mic).
//   - Firefox: NO pseudo-device at all; getUserMedia binds a real deviceId.
//   - BOTH, pre-permission: enumerateDevices() returns ONE blank entry
//     ({deviceId:'', groupId:'', label:''}) — not a device list. So nothing here
//     is meaningful until the mic has been acquired; that is the first snapshot.
//   - 'communications' is Windows-only and was NOT observable on macOS — it is
//     handled here on the documented behaviour, unverified by us.
// =====================================================================

// Synthetic entries that track the OS default instead of naming a physical mic.
// 'default': Chromium, all platforms. 'communications': Chromium on Windows.
const PSEUDO_DEVICE_IDS: ReadonlySet<string> = new Set(['default', 'communications']);

export function isPseudoDevice(deviceId: string): boolean {
  return PSEUDO_DEVICE_IDS.has(deviceId);
}

// =====================================================================
// Built-in vs external, derived from 30 days of real fleet labels (833 snapshots,
// 28 SDRs) rather than guessed. Two things that corpus settled:
//
//   - `External Microphone (Built-in)` is macOS's name for the HEADPHONE JACK. It
//     ends in "(Built-in)" and is external, so a suffix test is inverted for it.
//   - Windows names the laptop array `Microphone Array (<vendor>)` and the jack
//     `Microphone (<vendor>)` — one word apart, opposite meanings.
//
// Direction of error matters. Classifying a real built-in as external makes the
// warning tell an SDR to switch TO their laptop mic, so the built-in side is
// matched broadly (family patterns, not just the exact strings observed) and
// anything unrecognised falls through to external — the headset side is
// open-ended and cannot be enumerated.
// =====================================================================

// Exact labels observed as built-in. Localised names can't be pattern-matched
// (Intel's array reports as Spanish "Varios micrófonos …" for at least one SDR),
// so they are listed verbatim and this set grows as new locales appear.
const BUILT_IN_MIC_LABELS: ReadonlySet<string> = new Set([
  'Varios micrófonos (Intel® Smart Sound Technology for Digital Microphones)',
]);

// Apple laptop arrays. The `(Built-in)` suffix is optional because Firefox omits
// it ("MacBook Air Microphone") where Chromium includes it.
const APPLE_BUILT_IN = /^MacBook (Air|Pro) Microphone( \(Built-in\))?$/;
// Windows laptop arrays: "Microphone Array (Realtek(R) Audio)". Deliberately NOT
// /^Microphone \(/ — that form is the external jack.
const WINDOWS_BUILT_IN = /^Microphone Array\b/;

// Software devices that enumerate as inputs but are not microphones. Present on
// 5 SDRs permanently, so counting them as headsets is what makes a naive
// "more than one device" rule fire on ~30% of events.
// Matched by family, not by an allowlist: the OS tags every one of them with the
// same suffix (Teams, Splashtop, Loom, …), and an unlisted one falls through to
// `external` and gets nudged as a better mic despite carrying no audio input. No
// real headset ends this way, so the family match cannot steal one.
const VIRTUAL_INPUT_SUFFIX = /\(Virtual\)$/;

// Continuity / phone-as-mic entries drift in and out of the list as the handset
// moves in and out of range. Never a headset the SDR is meant to dial on.
const PHONE_MIC = /iPhone Microphone$/;

// Chrome's stand-in when it has a device but no label for it yet. Not a nameable
// device, so it can neither be a built-in nor something worth switching to.
const UNLABELLED = /^Unknown Audio Input Device\b/;

export type InputKind = 'built_in' | 'external' | 'virtual' | 'phone' | 'unlabelled';

export function classifyInput(label: string): InputKind {
  if (!label || UNLABELLED.test(label)) return 'unlabelled';
  if (VIRTUAL_INPUT_SUFFIX.test(label)) return 'virtual';
  if (PHONE_MIC.test(label)) return 'phone';
  if (BUILT_IN_MIC_LABELS.has(label) || APPLE_BUILT_IN.test(label) || WINDOWS_BUILT_IN.test(label))
    return 'built_in';
  return 'external';
}

// The structural subset of MediaDeviceInfo we use (MediaDeviceInfo satisfies it,
// so Twilio's availableInputDevices values pass straight in — and tests can use
// plain objects without constructing a real MediaDeviceInfo).
export interface RawDevice {
  deviceId: string;
  label: string;
  groupId: string;
}

export interface DeviceEntry extends RawDevice {
  pseudo: boolean;
}

export interface AudioSnapshot {
  // As Twilio reports it — may be a pseudo id like 'default'.
  selectedId: string | null;
  selectedLabel: string | null;
  selectedPseudo: boolean;
  // The PHYSICAL device behind the selection, resolved via groupId. Equal to the
  // selected* fields when the selection is already a real device (Firefox, or an
  // SDR who picked explicitly). null when we couldn't resolve — see resolveInput.
  resolvedId: string | null;
  resolvedLabel: string | null;
  // Every input the browser exposed, raw and unfiltered, pseudo entries included.
  // This is the corpus: we report labels verbatim so the built-in-vs-external
  // heuristic can be derived from real fleet data rather than guessed at.
  inputs: DeviceEntry[];
  // Physical mics only — pseudo entries excluded, so "how many mics does this SDR
  // actually have" is not inflated by Chromium's synthetic ones.
  realInputCount: number;
  // The label of an external mic that is CONNECTED while a built-in one is live —
  // i.e. the SDR has a headset plugged in and is dialing on the laptop mic anyway.
  // null when the active mic is already external, or nothing external is attached.
  idleExternalLabel: string | null;
}

// The connected-but-unused external mic, if the SDR is on a built-in one.
//
// Keys off the RESOLVED device, not the count of inputs: under a pseudo selection
// the raw id says nothing about which mic is live, and "more than one input" is
// the normal state on both platforms (Windows always lists the laptop array
// alongside a USB headset; a Mac with Teams installed always lists Teams).
function findIdleExternal(
  inputs: readonly DeviceEntry[],
  resolved: RawDevice | null,
): string | null {
  if (!resolved || classifyInput(resolved.label) !== 'built_in') return null;
  const idle = inputs.find((d) => !d.pseudo && classifyInput(d.label) === 'external');
  return idle?.label ?? null;
}

// Resolve a (possibly pseudo) deviceId to the real device behind it.
//   - a real deviceId resolves to itself
//   - a pseudo id resolves via groupId to the first real entry in the same group
//   - returns null if the id is absent, or is pseudo with no real group sibling
//     (seen when a pseudo entry is the ONLY entry, e.g. some virtual-audio setups)
export function resolveInput(inputs: readonly RawDevice[], deviceId: string): RawDevice | null {
  const selected = inputs.find((d) => d.deviceId === deviceId);
  if (!selected) return null;
  if (!isPseudoDevice(selected.deviceId)) return selected;
  return inputs.find((d) => !isPseudoDevice(d.deviceId) && d.groupId === selected.groupId) ?? null;
}

export function buildAudioSnapshot(
  inputs: readonly RawDevice[],
  selectedId: string | null,
): AudioSnapshot {
  const entries: DeviceEntry[] = inputs.map((d) => ({
    deviceId: d.deviceId,
    label: d.label,
    groupId: d.groupId,
    pseudo: isPseudoDevice(d.deviceId),
  }));
  const selected = selectedId ? (inputs.find((d) => d.deviceId === selectedId) ?? null) : null;
  const resolved = selectedId ? resolveInput(inputs, selectedId) : null;
  return {
    selectedId: selectedId ?? null,
    selectedLabel: selected?.label ?? null,
    selectedPseudo: selectedId ? isPseudoDevice(selectedId) : false,
    resolvedId: resolved?.deviceId ?? null,
    resolvedLabel: resolved?.label ?? null,
    inputs: entries,
    realInputCount: entries.filter((d) => !d.pseudo).length,
    idleExternalLabel: findIdleExternal(entries, resolved),
  };
}

// Dedupe key for "has anything we care about actually changed?".
//
// Built from the RESOLVED device plus the set of real devices — deliberately NOT
// from the raw selected id. Under a pseudo-device the raw id is constant ('default'
// before AND after a headset unplug), so a raw-id key would suppress the single
// most important event we are trying to capture. The resolved id moves when the
// physical mic moves, which is the thing worth an event.
//
// The device set is included so a plug/unplug that does NOT change the active mic
// (a second headset appearing — the case the UI warning will key on) still reports.
export function snapshotKey(s: AudioSnapshot): string {
  const present = s.inputs
    .filter((d) => !d.pseudo)
    .map((d) => d.deviceId)
    .sort()
    .join(',');
  return `${s.resolvedId ?? s.selectedId ?? 'none'}|${present}`;
}
